import React, { useState } from "react";
import * as moment from "moment";
import { Link } from "react-router-dom";
import {
  ListItemText,
  ListItem,
  ListItemSecondaryAction,
  ListItemAvatar,
  Avatar,
  IconButton,
  Divider,
  Box,
  Typography,
  Grid,
  Menu,
  MenuItem,
  Grow,
} from "@material-ui/core";
import CancelIcon from "@material-ui/icons/Cancel";
import FileCopy from "@material-ui/icons/FileCopy";
import { Draggable } from "react-beautiful-dnd";
import WarningRoundedIcon from "@material-ui/icons/WarningRounded";
import MoreHorizIcon from "@material-ui/icons/MoreHoriz";

import makeStyles from "@material-ui/core/styles/makeStyles";
import Tooltip from "@material-ui/core/Tooltip";
import { Edit } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";

const useStyles = makeStyles((theme) => ({
  draggingListItem: {
    background: "rgba(255, 255, 255, 0.1)",
    borderRadius: "12px",
    marginBottom: "8px",
  },
  listItem: {
    background: "rgba(255, 255, 255, 0.05)",
    borderRadius: "12px",
    marginBottom: "8px",
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)",
    },
  },
  listItemSelected: {
    background: "rgba(0, 0, 0, 0.05)",
    borderRadius: "12px",
    marginBottom: "8px",
    border: "1.3px solid #60A5FA",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.08)",
    },
  },
  deleteIcon: {
    color: "#EF4444",
    [theme.breakpoints.down("sm")]: {
      width: 40,
      height: 40,
    },
  },
  icon: {
    color: "rgba(255, 255, 255, 0.7)",
    [theme.breakpoints.down("sm")]: {
      width: 40,
      height: 40,
    },
  },
  iconAlert: {
    color: "#EF4444",
    [theme.breakpoints.down("sm")]: {},
  },
  warningIcon: {
    marginLeft: "20px",
    height: "100%",
  },
  listItemText: {
    "& .MuiListItemText-primary": {
      color: "#000000",
      fontWeight: 500,
      fontSize: "0.95rem",
    },
    "& .MuiListItemText-secondary": {
      color: "rgba(0, 0, 0, 0.7)",
      fontSize: "0.85rem",
    },
  },
  listItemTextTwo: {
    "& .MuiListItemText-primary": {
      color: "#000000",
      fontWeight: 500,
      fontSize: "0.95rem",
    },
    "& .MuiListItemText-secondary": {
      color: "rgba(0, 0, 0, 0.7)",
      fontSize: "0.85rem",
    },
  },
  hasAbo: {
    color: "#10B981",
  },
  noAbo: {
    color: "#EF4444",
  },
  admin: {
    color: "white",
  },
  innactive: {
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    opacity: 0.7,
  },
  secondaryActions: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    height: "100%",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "center",
      width: 12,
    },
    [theme.breakpoints.down("xs")]: {
      marginLeft: 18,
    },
  },
  moreButton: {
    padding: 4,
    display: "flex",
    alignItems: "center",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.1)",
      color: "#000000",
      borderRadius: "50%",
    },
  },
  menu: {
    "& .MuiPaper-root": {
      backgroundColor: "#1F2937",
      boxShadow:
        "0px 4px 6px -1px rgba(0, 0, 0, 0.2), 0px 2px 4px -1px rgba(0, 0, 0, 0.1)",
      borderRadius: "12px",
      marginTop: "4px",
      marginLeft: "48px",
      border: "1px solid rgba(255, 255, 255, 0.1)",
      transition: "transform 0.2s ease-out, opacity 0.2s ease-out",
      transformOrigin: "0 0",
    },
  },
  menuItem: {
    padding: "12px 16px",
    minWidth: "180px",
    color: "white",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)",
    },
    "& .MuiSvgIcon-root": {
      fontSize: "20px",
    },
  },
  menuItemText: {
    marginLeft: "12px",
    fontSize: "14px",
    fontWeight: 500,
    color: "white",
  },
  menuItemEdit: {
    "& .MuiSvgIcon-root": {
      color: "#60A5FA",
    },
    "&:hover": {
      backgroundColor: "rgba(96, 165, 250, 0.1)",
    },
  },
  menuItemDuplicate: {
    "& .MuiSvgIcon-root": {
      color: "#34D399",
    },
    "&:hover": {
      backgroundColor: "rgba(52, 211, 153, 0.1)",
    },
  },
  menuItemDelete: {
    "& .MuiSvgIcon-root": {
      color: "#EF4444",
    },
    "&:hover": {
      backgroundColor: "rgba(239, 68, 68, 0.1)",
    },
  },
  avatar: {
    backgroundColor: "#3B82F6",
    color: "white",
    "&:hover": {
      backgroundColor: "#2563EB",
    },
  },
  switchVisibility: {
    display: "inline",
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
}));

const DraggableListItem = ({
  item,
  index,
  onDelete,
  onDuplicate,
  onEdit,
  onClick,
  selectedId,
  textColor = "black",
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenuClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setAnchorEl(null);
  };

  const handleEdit = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onEdit(item.idInt);
    handleMenuClose();
  };

  const handleDuplicate = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onDuplicate(item.idInt);
    handleMenuClose();
  };

  const handleDelete = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onDelete(item.idInt);
    handleMenuClose();
  };

  const CustomLink = React.useMemo(
    () =>
      React.forwardRef((linkProps, ref) => (
        <Link ref={ref} to={item.to} {...linkProps} />
      )),
    [item.to]
  );

  return (
    <Draggable draggableId={item.id} index={index}>
      {(provided, snapshot) => (
        <ListItem
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={
            item.isActive === false
              ? classes.innactive
              : snapshot.isDragging
              ? classes.draggingListItem
              : selectedId !== undefined && selectedId === item.idInt
              ? classes.listItemSelected
              : classes.listItem
          }
          button
          component={onClick ? null : CustomLink}
          onClick={onClick ? () => onClick(item.idInt) : null}
        >
          <ListItemAvatar className={classes.switchVisibility}>
            <Avatar className={classes.avatar}>{item.icon}</Avatar>
          </ListItemAvatar>
          <Grid container spacing={2}>
            <Grid
              item
              style={{
                display: "flex",
                alignItems: "center",
              }}
              xs={!onEdit && !onEdit && !item.language ? 8 : 12}
              sm={8}
            >
              <Grid item xs={12} md={item.createdAt && item.language ? 6 : 9}>
                <ListItemText
                  primary={item.primary}
                  secondary={item.secondary}
                  classes={{
                    primary: classes.listItemText,
                    secondary: classes.listItemText,
                  }}
                  style={{ color: textColor }}
                  primaryTypographyProps={{
                    style: {
                      fontSize: "1rem",
                      fontWeight: 500,
                    },
                  }}
                  secondaryTypographyProps={{
                    style: {
                      fontSize: "0.875rem",
                      color: "rgba(0, 0, 0, 0.6)",
                    },
                  }}
                />
              </Grid>
              {item.primaryTwo && item.secondaryTwo ? (
                <Grid item xs={12} md={3}>
                  <ListItemText
                    primary={item.primaryTwo}
                    secondary={item.secondaryTwo}
                    style={{ color: textColor }}
                    classes={{
                      primary: classes.listItemTextTwo,
                      secondary: classes.listItemTextTwo,
                    }}
                  />
                </Grid>
              ) : (
                <Grid item xs={1}></Grid>
              )}
              {item.role ? (
                <Grid item xs={12} md={3}>
                  <ListItemText
                    primary={
                      item.role === "PACK_DECOUVERTE"
                        ? "Pack découverte"
                        : item.role === "PACK_ESSAIE"
                        ? "Pack essai"
                        : item.role === "PACK_AVENTURE"
                        ? "Pack aventure"
                        : item.role === "PACK_EXPERT"
                        ? "Pack expert"
                        : item.role === "NO_SUBSCRIBTION"
                        ? "Pas de souscription"
                        : item.role === "NEW_MEMBER"
                        ? "Nouveau membre"
                        : item.role === "ADMIN"
                        ? "Admin"
                        : "ROLE INCONNUE"
                    }
                    secondary=""
                    classes={{
                      primary:
                        item.role === "NO_SUBSCRIBTION" ||
                        item.role === "NEW_MEMBER"
                          ? classes.noAbo
                          : item.role === "ADMIN"
                          ? classes.admin
                          : classes.hasAbo,
                      secondary: classes.listItemTextTwo,
                    }}
                    style={{ color: "blue" }}
                  />
                </Grid>
              ) : (
                <Grid item xs={1}></Grid>
              )}

              {item.createdAt && item.language ? (
                <Grid item xs={12} md={3}>
                  <ListItemText
                    primary={moment(item.createdAt).format("DD/MM/YYYY")}
                    secondary={item.language}
                    classes={{
                      primary: classes.listItemTextTwo,
                      secondary: classes.listItemTextTwo,
                    }}
                  />
                </Grid>
              ) : (
                <></>
              )}
            </Grid>
            <Grid
              item
              xs={!onEdit && !onEdit && !item.language ? 4 : 12}
              sm={4}
            >
              <div className={classes.secondaryActions}>
                {!item.isValid && (
                  <Tooltip
                    className={classes.warningIcon}
                    title={t("draggable_list_item.missing_data")}
                  >
                    <WarningRoundedIcon className={classes.iconAlert} />
                  </Tooltip>
                )}
                <IconButton
                  onClick={handleMenuClick}
                  size="small"
                  className={classes.moreButton}
                  style={{ color: textColor }}
                >
                  <MoreHorizIcon />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  keepMounted
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                  className={classes.menu}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                  TransitionComponent={Grow}
                  TransitionProps={{
                    timeout: 200,
                    style: {
                      transformOrigin: "left top",
                    },
                  }}
                >
                  {onEdit && (
                    <MenuItem
                      onClick={handleEdit}
                      className={`${classes.menuItem} ${classes.menuItemEdit}`}
                    >
                      <Edit />
                      <span className={classes.menuItemText}>
                        {t("draggable_list_item.edit")}
                      </span>
                    </MenuItem>
                  )}
                  {onDuplicate && (
                    <MenuItem
                      onClick={handleDuplicate}
                      className={`${classes.menuItem} ${classes.menuItemDuplicate}`}
                    >
                      <FileCopy />
                      <span className={classes.menuItemText}>
                        {t("draggable_list_item.duplicate")}
                      </span>
                    </MenuItem>
                  )}
                  {onDelete && (
                    <MenuItem
                      onClick={handleDelete}
                      className={`${classes.menuItem} ${classes.menuItemDelete}`}
                    >
                      <CancelIcon />
                      <span className={classes.menuItemText}>
                        {t("draggable_list_item.delete")}
                      </span>
                    </MenuItem>
                  )}
                </Menu>
              </div>
            </Grid>
          </Grid>
        </ListItem>
      )}
    </Draggable>
  );
};

export default DraggableListItem;
